import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UserContext from '../context/UserContext';
import {
    PUSH_PROMPT_STATUS_KEY,
    enablePushNotifications,
    handleIncomingPushPayload,
    isFirebaseMessagingSupported,
    isPushNotificationSupported,
    subscribeToForegroundMessages,
    syncPushTokenIfGranted
} from '../util/pushNotifications';
import { playNotificationSound } from '../util/notificationSound';
import { dispatchStudentNotificationReceived } from '../util/studentNotificationEvents';

/**
 * Prompts the student to enable push notifications after login.
 */
const PushNotificationPrompt = () => {
    const { user } = useContext(UserContext);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [foregroundAlert, setForegroundAlert] = useState(null);

    const shouldShowPrompt = useCallback(() => {
        if (!isPushNotificationSupported()) {
            return false;
        }
        const permission = Notification.permission;
        if (permission === 'granted' || permission === 'denied') {
            return false;
        }
        const status = localStorage.getItem(PUSH_PROMPT_STATUS_KEY);
        return status !== 'declined';
    }, []);

    useEffect(() => {
        if (!user) {
            return undefined;
        }

        let unsubscribe = null;

        const init = async () => {
            const messagingSupported = await isFirebaseMessagingSupported();
            if (!messagingSupported) {
                console.warn('Firebase Cloud Messaging is not supported in this browser.');
                return;
            }

            if (Notification.permission === 'granted') {
                const syncResult = await syncPushTokenIfGranted();
                if (!syncResult.synced) {
                    console.warn('FCM token was not synced:', syncResult.reason, syncResult.error);
                }
            } else if (Notification.permission === 'default' && shouldShowPrompt()) {
                setOpen(true);
            }

            unsubscribe = await subscribeToForegroundMessages((payload) => {
                const { title, body } = handleIncomingPushPayload(payload);
                playNotificationSound();
                dispatchStudentNotificationReceived({
                    title,
                    body,
                    notificationId: payload.data?.notificationId
                });
                setForegroundAlert({ title, body });
            });
        };

        init();

        const onServiceWorkerMessage = (event) => {
            if (event.data?.type !== 'student-notification-received') {
                return;
            }
            playNotificationSound();
            dispatchStudentNotificationReceived({
                title: event.data.title,
                body: event.data.body,
                notificationId: event.data.notificationId
            });
            if (event.data.title) {
                const title = event.data.title;
                const body = event.data.body || '';
                handleIncomingPushPayload({
                    notification: { title, body },
                    data: event.data
                });
                setForegroundAlert({ title, body });
            }
        };

        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
        }

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
            }
        };
    }, [user, shouldShowPrompt]);

    const handleEnable = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await enablePushNotifications();
            if (result.success) {
                setOpen(false);
                return;
            }
            if (result.reason === 'denied') {
                setError('Notifications are blocked in your browser. Enable them in site settings to receive updates.');
            } else if (result.reason === 'not_configured') {
                setError('Push notifications are not configured yet. Please contact support.');
            } else if (result.reason === 'dismissed') {
                setOpen(false);
            } else if (result.reason === 'token_error') {
                setError(result.error || 'Could not register this device for push notifications.');
            } else {
                setError(`Could not enable notifications (${result.reason || 'unknown'}). Please try again.`);
            }
        } catch (err) {
            console.error('Enable push notifications failed', err);
            setError('Could not enable notifications. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDecline = () => {
        localStorage.setItem(PUSH_PROMPT_STATUS_KEY, 'declined');
        setOpen(false);
    };

    if (!user) {
        return null;
    }

    return (
        <>
            {foregroundAlert && (
                <Alert
                    variant="soft"
                    color="warning"
                    sx={{
                        position: 'fixed',
                        top: { xs: 72, sm: 80 },
                        right: 16,
                        left: { xs: 16, sm: 'auto' },
                        zIndex: 1400,
                        maxWidth: 420
                    }}
                    endDecorator={
                        <Button size="sm" variant="plain" onClick={() => setForegroundAlert(null)}>
                            Dismiss
                        </Button>
                    }
                >
                    <Typography level="title-sm">{foregroundAlert.title}</Typography>
                    {foregroundAlert.body && (
                        <Typography level="body-sm">{foregroundAlert.body}</Typography>
                    )}
                </Alert>
            )}

            <Modal open={open} onClose={handleDecline}>
                <ModalDialog
                    variant="outlined"
                    role="alertdialog"
                    aria-labelledby="push-notification-title"
                    sx={{ maxWidth: 440 }}
                >
                    <ModalClose />
                    <Stack spacing={2}>
                        <NotificationsActiveIcon sx={{ fontSize: 40, color: '#eb7a24' }} />
                        <Typography id="push-notification-title" level="h4">
                            Enable notifications
                        </Typography>
                        <Typography level="body-md">
                            Stay updated with your class schedule, tests, and other important messages from Vector India.
                        </Typography>
                        {error && (
                            <Alert color="danger" variant="soft">
                                {error}
                            </Alert>
                        )}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="plain" color="neutral" onClick={handleDecline} disabled={loading}>
                                Not now
                            </Button>
                            <Button
                                onClick={handleEnable}
                                loading={loading}
                                sx={{
                                    bgcolor: '#eb7a24',
                                    '&:hover': { bgcolor: '#e55125' }
                                }}
                            >
                                Enable notifications
                            </Button>
                        </Stack>
                    </Stack>
                </ModalDialog>
            </Modal>
        </>
    );
};

export default PushNotificationPrompt;
